/**
 * src/data/pricingFallback.js
 *
 * Static pricing catalog used when the live pricing API/DB is unavailable.
 * Kept structurally close to whatever the real pricing service returns
 * (plans + groups + meta) so callers don't have to branch on source.
 */

export const PRICING_FALLBACK = {
  plans: [
    {
      id: 'cto',
      name: 'Fractional CTO',
      group: 'retainer',
      priceCents: 150000,
      priceLabel: '$1,500/mo',
      billing: 'monthly',
      contractMonths: null,
      prepayTotalCents: 400000,
      prepayMonths: 3,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Architecture & technical strategy',
        'Hiring, mentoring & code standards',
        'Roadmap & delivery oversight',
        'Vendor & infrastructure decisions',
        'Investor / due-diligence support',
      ],
      badge: '3 months for $4,000',
      description: 'A part-time CTO who owns your technical strategy, hiring, and delivery without the full-time cost.',
      sort: 10,
      active: true,
    },
    {
      id: 'cmo',
      name: 'Fractional CMO',
      group: 'retainer',
      priceCents: 150000,
      priceLabel: '$1,500/mo',
      billing: 'monthly',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Positioning & messaging',
        'Campaign planning across channels',
        'Content & ad direction',
        'Funnel & analytics reviews',
        'Monthly growth report',
      ],
      badge: null,
      description: 'A part-time CMO who owns positioning, campaigns, and growth reporting month to month.',
      sort: 20,
      active: true,
    },
    {
      id: 'cto-cmo',
      name: 'CTO + CMO',
      group: 'retainer',
      priceCents: 250000,
      priceLabel: '$2,500/mo',
      billing: 'monthly',
      contractMonths: 12,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Both roles, one team',
        'Unlimited requests',
        '12-month contract, paid monthly',
        'Weekly leadership sync',
        'Priority delivery',
      ],
      badge: 'Unlimited requests',
      description: 'Both fractional roles under one 12-month contract, with unlimited requests and priority delivery.',
      sort: 30,
      active: true,
    },
    {
      id: 'site-devops',
      name: 'Website + DevOps',
      group: 'website',
      priceCents: 100000,
      priceLabel: '$1,000',
      billing: 'one-time',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Custom design & build',
        'Deployed on your domain',
        'CI/CD, monitoring & backups',
        'SSL, DNS, uptime',
        '30 days post-launch support',
      ],
      badge: null,
      description: 'A custom-built, deployed website with CI/CD, monitoring, and 30 days of support after launch.',
      sort: 40,
      active: true,
    },
    {
      id: 'site-devops-mail',
      name: 'Website + DevOps + Hosting & Mailing',
      group: 'website',
      priceCents: 150000,
      priceLabel: '$1,500',
      billing: 'one-time',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Everything in Website + DevOps',
        'Managed hosting',
        'Unlimited inboxes on your domain',
        'Every email verified before sending — protects sender reputation',
        'Spam protection',
      ],
      badge: 'Most popular',
      description: 'Everything in Website + DevOps, plus managed hosting and unlimited, reputation-safe email inboxes.',
      sort: 50,
      active: true,
    },
    {
      id: 'site-seo',
      name: 'Website + DevOps + Mailing + SEO',
      group: 'website',
      priceCents: 200000,
      priceLabel: '$2,000',
      billing: 'one-time',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Everything in Hosting & Mailing',
        'Keyword map by intent',
        'Hub-and-spoke content plan',
        'Local SEO, GBP & citations',
        'Quarterly backlink-gap runs + 90-day plan',
      ],
      badge: null,
      description: 'Everything in Hosting & Mailing, plus a full SEO buildout with a recurring quarterly plan.',
      sort: 60,
      active: true,
    },
    {
      id: 'feature',
      name: 'Extra feature',
      group: 'addon',
      priceCents: null,
      priceLabel: 'from $100',
      billing: 'one-time',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: false,
      features: [],
      badge: null,
      description: 'A one-off feature added to an existing site or app, scoped and quoted individually.',
      sort: 70,
      active: true,
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      group: 'addon',
      priceCents: 50000,
      priceLabel: '$500',
      billing: 'one-time',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [],
      badge: null,
      description: 'A custom admin or analytics dashboard added on top of an existing product.',
      sort: 80,
      active: true,
    },
    {
      id: 'growth',
      name: 'Growth',
      group: 'growth',
      priceCents: 200000,
      priceLabel: '$2,000/mo',
      billing: 'monthly',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Unlimited features',
        'Continuous iteration',
        'Priority support',
        'Monthly roadmap',
      ],
      badge: null,
      description: 'An ongoing monthly plan for unlimited feature work and continuous iteration on your product.',
      sort: 90,
      active: true,
    },
    {
      id: 'growth-plus',
      name: 'Growth+',
      group: 'growth',
      priceCents: 300000,
      priceLabel: '$3,000/mo',
      billing: 'monthly',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Everything in Growth',
        'Branding assets',
        'Full-suite marketing management',
        '3 platforms',
      ],
      badge: 'Branding included',
      description: 'Everything in Growth, plus branding assets and full-suite marketing management across 3 platforms.',
      sort: 100,
      active: true,
    },
    {
      id: 'growth-max',
      name: 'Growth Max',
      group: 'growth',
      priceCents: 350000,
      priceLabel: '$3,500/mo',
      billing: 'monthly',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: true,
      features: [
        'Everything in Growth+',
        '6 platforms',
        'Dedicated marketing lead',
      ],
      badge: null,
      description: 'Everything in Growth+, scaled to 6 platforms with a dedicated marketing lead.',
      sort: 110,
      active: true,
    },
    {
      id: 'ads',
      name: 'Ad management',
      group: 'ads',
      priceCents: 50000,
      priceLabel: '$500/mo under $5k budget · 10% of spend from $5k',
      billing: 'percent',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: 0.10,
      percentMinBudgetCents: 500000,
      depositPercent: 50,
      checkoutable: false,
      features: [
        'Google first page, 24/7',
        'Meta & LinkedIn campaigns',
        'Landing page + tracking',
        'Weekly optimisation',
        'Fee tied to spend',
      ],
      badge: null,
      description: 'Full-funnel ad management with a management fee tied directly to your ad spend.',
      sort: 120,
      active: true,
    },
    {
      id: 'ops-stack',
      name: 'Ops Stack',
      group: 'ops',
      priceCents: 100000,
      priceLabel: 'from $1,000',
      billing: 'custom',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: false,
      features: [
        'Hosting',
        'Mailing addresses & inboxes',
        'Spam protection',
        'Free mail verification',
        'Email marketing',
        'Brand visuals',
        'Generative-AI automation across platforms',
      ],
      badge: null,
      description: 'The full back-office stack — hosting, mail, spam protection, and AI automation — bundled and custom-scoped.',
      sort: 130,
      active: true,
    },
    {
      id: 'hermes',
      name: 'Hermes agent harness',
      group: 'product',
      priceCents: null,
      priceLabel: 'Custom',
      billing: 'custom',
      contractMonths: null,
      prepayTotalCents: null,
      prepayMonths: null,
      percentOfSpend: null,
      percentMinBudgetCents: null,
      depositPercent: 50,
      checkoutable: false,
      features: [
        'Hosted by Bluprise — no Mac or VPS needed',
        'Unlimited use',
        'Your workflows, automated',
      ],
      badge: null,
      description: 'A Bluprise-hosted agent harness that runs your automated workflows without your own infrastructure.',
      sort: 140,
      active: true,
    },
  ],
  groups: [
    { id: 'retainer', label: 'Fractional leadership', order: 1 },
    { id: 'website', label: 'Website packages', order: 2 },
    { id: 'growth', label: 'Growth plans', order: 3 },
    { id: 'ads', label: 'Ad management', order: 4 },
    { id: 'ops', label: 'Ops Stack', order: 5 },
    { id: 'addon', label: 'Add-ons', order: 6 },
    { id: 'product', label: 'Products', order: 7 },
  ],
  meta: {
    depositPercent: 50,
    checkoutEnabled: false,
  },
};
